import React, { useMemo } from "react";
import { useTheme } from "styled-components";

import { HOMEPAGE_V2 } from "@config/testIds/app";
import {
  BATMAID_FACEBOOK_LINKS,
  BATMAID_INSTAGRAM_LINKS,
  BATMAID_LINKEDIN_LINK,
  BATMAID_TWITTER_LINK,
  BATMAID_YOUTUBE_LINK,
} from "@config/consts";

import { AVAILABLE_COUNTRIES } from "@typings/globals";

import { Text, TestWrapper } from "@ui/Atoms";
import {
  Facebook,
  YouTube,
  Instagram,
  LinkedIn,
  X,
} from "@ui/Assets/SocialIcons";

import { Markdown } from "@containers/Markdown";

import { useTranslate } from "@hooks/useTranslate";

import { FooterLink } from "../FooterLink";

import * as Styled from "./LegalSocials.styled";

const socialLinks = (country: AVAILABLE_COUNTRIES) => [
  {
    href:
      BATMAID_FACEBOOK_LINKS.get(country) ||
      BATMAID_FACEBOOK_LINKS.get("DEFAULT"),
    icon: Facebook,
    testId: "footer-social-facebook",
  },
  {
    href: BATMAID_LINKEDIN_LINK,
    icon: LinkedIn,
    testId: "footer-social-linkedin",
  },
  {
    href:
      BATMAID_INSTAGRAM_LINKS.get(country) ||
      BATMAID_INSTAGRAM_LINKS.get("DEFAULT"),
    icon: Instagram,
    testId: "footer-social-instagram",
  },
  {
    href: BATMAID_TWITTER_LINK,
    icon: X,
    testId: "footer-social-twitter",
  },
  {
    href: BATMAID_YOUTUBE_LINK,
    icon: YouTube,
    testId: "footer-social-youtube",
  },
];

export const LegalSocials: React.FC<{ country: AVAILABLE_COUNTRIES }> = ({
  country,
}) => {
  const theme = useTheme();
  const translate = useTranslate("batmaid_pwa_footer");
  const currentYear = useMemo(() => new Date().getFullYear(), []);

  const SOCIAL_ICON_STYLE = {
    size: 24,
    height: 24,
    color: theme.palette.white,
    style: {
      height: 24,
    },
  };

  return (
    <Styled.Root>
      <TestWrapper testId={HOMEPAGE_V2.FOOTER_LEGAL}>
        <Styled.LegalContainer>
          <Text $style="regular_14" $color="white">
            {translate("copyrights", {
              year: currentYear,
            })}
          </Text>
          <Markdown
            components={{
              a: props => <FooterLink {...props} />,
              ul: props => <Styled.LegalLinksList {...props} />,
              li: props => <Styled.LegalLink {...props} />,
            }}
          >
            {translate("legal_links")}
          </Markdown>
        </Styled.LegalContainer>
      </TestWrapper>
      <TestWrapper testId={HOMEPAGE_V2.FOOTER_SOCIAL}>
        <Styled.SocialContainer>
          <Text $style="regular_14" $color="white">
            <Markdown
              components={{
                a: props => <FooterLink {...props} />,
              }}
            >
              {translate("hashtags")}
            </Markdown>
          </Text>
          <Styled.SocialList>
            {socialLinks(country).map(({ href, icon: Icon, testId }) => (
              <TestWrapper key={testId} testId={testId}>
                <a href={href} target="_blank" rel="noopener noreferrer">
                  <Icon {...SOCIAL_ICON_STYLE} />
                </a>
              </TestWrapper>
            ))}
          </Styled.SocialList>
        </Styled.SocialContainer>
      </TestWrapper>
    </Styled.Root>
  );
};
